import Discovery, { events as DiscoveryEvents } from '@ringcentral/sdk/lib/platform/Discovery';
import FfsController from '@ringcentral/rwc-utils/src/feature-flag/controller/ffs/ffs-controller';
import { getBrandId } from '@ringcentral/rwc-branding/src/utils';
import eventBus, { SIGN_OUT } from '@ringcentral/rwc-utils/src/event-bus';
import { FFSFetchFn } from '@ringcentral/rwc-utils/src/feature-flag/types';
import Platform from '@ringcentral/sdk/lib/platform/Platform';
import {
    createPlatformSDKFetchFailureHandler,
    fetchSuccessHandler,
} from '@rwc/utils/fetch-handlers';
import { captureMessageLazily } from '@low/utils/lazy-sentry';

const ffsInstance = FfsController.getInstance();
/**
 * handle the sequence of ffs initialization
 * 1.Ffs needs to be requested after discovery ends
 * 2.When discovery result updated, need to pass the new ffs public server and ffs auth server to ffs controller
 * 3.In hercules mode, RCV get the discovery result from jupiter, and add listener event
 */
class FfsConnector {
    private static instance: FfsConnector | null;

    private platform!: Platform;

    private discovery: Discovery | null = null;

    public static getInstance() {
        if (!FfsConnector.instance) {
            FfsConnector.instance = new FfsConnector();
        }

        return FfsConnector.instance;
    }

    setFfsAuthServer(externalDiscoveryData) {
        const ffsAuthServer = externalDiscoveryData?.featureFlagsApi?.baseUri;
        if (ffsAuthServer) {
            ffsInstance.setFfsAuthServer(ffsAuthServer);
        } else {
            captureMessageLazily(
                () =>
                    `The ffs server from external data is empty: ${JSON.stringify(
                        externalDiscoveryData
                    )}`
            );
        }
    }

    onLogoutSuccess() {
        ffsInstance.identify(null);
    }

    removeListener() {
        this.discovery?.removeListener(DiscoveryEvents.externalDataUpdated, this.setFfsAuthServer);

        eventBus.removeListener(SIGN_OUT, this.onLogoutSuccess);
    }

    async init(platform: Platform) {
        this.platform = platform;
        const fetch: FFSFetchFn = options =>
            this.platform
                .send(options)
                .then(fetchSuccessHandler, createPlatformSDKFetchFailureHandler('FFS init error'));
        const brandId = getBrandId();
        ffsInstance.init(fetch, brandId);

        this.discovery = this.platform.discovery();
        const externalData = await this.discovery.externalData();
        if (externalData) {
            this.setFfsAuthServer(externalData);
        } else {
            this.discovery.refreshExternalData();
        }

        this.discovery.on(DiscoveryEvents.externalDataUpdated, this.setFfsAuthServer);
        eventBus.on(SIGN_OUT, this.onLogoutSuccess);
    }

    dispose() {
        ffsInstance.dispose();
        this.removeListener();
    }
}

export default FfsConnector.getInstance();
